import dayjs, { Dayjs } from 'dayjs'
import { RefObject, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import { getMostDisplayedIdInDOM } from './get-most-displayed-id-in-dom'
import { useLocale } from '../../../../../../../hooks/use-locale'
import { HouseSeasonalBusynessCalendarMonths } from '../../../../../../../lib/graphql'
import { monthDefaultMessages } from '../../../../../../../utils/month/constants'
import { ScrollbarRef } from '../../../../../../layout'

export const BASE_MONTH_VIEW_ID = 'rcd-house-availability'
const BIG_CALENDAR_MAX_MONTHS = 18
const TODAY = dayjs().startOf('day')

export type BigCalendarMonth = {
  id: string
  date: Dayjs
  label: string
  offSet: number
  seasonalBusynessKey: keyof Omit<
    HouseSeasonalBusynessCalendarMonths,
    '__typename'
  >
}

export type UseBigCalendarContextProps = {
  locale: string
  calendarMonths: BigCalendarMonth[]
  currentMonth: BigCalendarMonth
  handleOnScroll: () => void
  scrollbarsRef: RefObject<ScrollbarRef>
}
export const useBigCalendar = (): UseBigCalendarContextProps => {
  const { locale } = useLocale()
  const intl = useIntl()

  const scrollbarsRef = useRef<ScrollbarRef>(null)

  const computeMonth = (date: Dayjs = TODAY): BigCalendarMonth => {
    const idSuffix = date.startOf('month').format('MMYYYY')
    const id = `${BASE_MONTH_VIEW_ID}-${idSuffix}`

    const offSet = document.getElementById(id)?.offsetTop ?? 0

    const monthKey = date
      .locale('en')
      .format('MMMM')
      .toLocaleLowerCase() as keyof typeof monthDefaultMessages

    if (!(monthKey in monthDefaultMessages)) {
      throw new Error(`Month key ${monthKey} is not in monthDefaultMessages`)
    }

    const label = `${intl.formatMessage(monthDefaultMessages[monthKey])} ${date.format('YYYY')}`

    const seasonalBusynessKey = date
      .locale('en')
      .format('MMMM')
      .toLowerCase() as keyof Omit<
      HouseSeasonalBusynessCalendarMonths,
      '__typename'
    >

    return { id, date, label, offSet, seasonalBusynessKey }
  }

  const computeCurrentMonthView = (date?: Dayjs): BigCalendarMonth => {
    if (date) {
      return computeMonth(date)
    }

    const id = getMostDisplayedIdInDOM()

    if (!id) {
      return computeMonth()
    }

    const monthYear = id.split('-').slice(-1)[0]

    const computedDate = dayjs(monthYear, 'MMYYYY')

    return computeMonth(computedDate)
  }

  const { date } = computeCurrentMonthView()

  const [currentMonth, setCalendarMonth] = useState<BigCalendarMonth>(() =>
    computeMonth(date),
  )

  const handleOnScroll = () => {
    setCalendarMonth(() => computeCurrentMonthView())
  }

  const calendarMonthsDates = Array.from(
    { length: BIG_CALENDAR_MAX_MONTHS },
    (_, i) => TODAY.add(i, 'month'),
  )
  const calendarMonths = calendarMonthsDates.map((date) => computeMonth(date))

  return { locale, calendarMonths, currentMonth, handleOnScroll, scrollbarsRef }
}
